import { useEffect, useRef, useState } from "react";
import { getSprintDados, getSprintDevs, getProgramados, clearCache, atribuirTicket, updateTicketStatus } from "../api/sprint.api";
import { getSetupResponsaveis, putTicketResponsavel } from "../api/setup.api";

const REFRESH_SEC = 40;

const COLUNAS = [
  { keys: ["Aguardando Desenvolvimento", "Parado", "Retrabalhar", "Especificação Ajustada"], label: "Aguardando Desenvolvimento", color: "#38bdf8" },
  { keys: ["Em curso"],                                             label: "Em Desenvolvimento",          color: "#fbbf24" },
  { keys: ["Resolvido", "Fechado", "Em Teste", "Aguardando Testes", "Feedback", "Aguardando Versão", "Ajuste Especificação"], label: "Resolvido", color: "#4ade80" },
];

const STATUS_KEYS = new Set(COLUNAS.flatMap(c => c.keys));

const DEVS_FIXOS = ["Rudi Monteiro", "Marcelo Rossi", "Paulo Lima", "Guilherme Martin"];

const DEV_ALIASES = {
  "Rudi Monteiro": "Rudi",
};

/* ── Visual helpers ──────────────────────────────────────────────── */

const KANBAN_AVATAR_MAP = [
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
];
function kanbanAvatarCores(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const low = nome.toLowerCase();
  for (const m of KANBAN_AVATAR_MAP) if (low.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function kanbanAvatarInitial(nome) {
  if (!nome) return "?";
  const parts = nome.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
}

const KAN_COL_STYLES = {
  "Aguardando Desenvolvimento": {
    bg: "rgba(56,189,248,0.05)",
    borderTop: "2px solid rgba(56,189,248,0.4)",
    textColor: "#38bdf8",
    pillBg: "rgba(56,189,248,0.15)",
    pillColor: "#38bdf8",
  },
  "Em Desenvolvimento": {
    bg: "rgba(186,117,23,0.05)",
    borderTop: "2px solid rgba(186,117,23,0.4)",
    textColor: "#FAC775",
    pillBg: "rgba(186,117,23,0.2)",
    pillColor: "#FAC775",
  },
  "Resolvido": {
    bg: "rgba(29,158,117,0.05)",
    borderTop: "2px solid rgba(29,158,117,0.4)",
    textColor: "#5DCAA5",
    pillBg: "rgba(29,158,117,0.2)",
    pillColor: "#5DCAA5",
  },
};

function kanbanPctColor(pct) {
  if (pct >= 90) return "#1D9E75";
  if (pct >= 70) return "#EF9F27";
  return "#E24B4A";
}

function kanbanBorderColor(ticket) {
  const p = ticket.programado;
  if (p === "1" || p === 1 || p === true)  return "#22d3ee";
  if (p === "0" || p === 0 || p === false) return "#f87171";
  return "#475569";
}

function kanbanIdColor(ticket) {
  const p = ticket.programado;
  if (p === "1" || p === 1 || p === true)  return "#38bdf8";
  if (p === "0" || p === 0 || p === false) return "#f87171";
  return "rgba(255,255,255,0.35)";
}

function buildDevList(tickets) {
  const map = new Map();
  for (const t of tickets) {
    const dev = t.desenvolvedor || "Não atribuído";
    if (!map.has(dev)) map.set(dev, { nome: dev, tickets: [] });
    map.get(dev).tickets.push(t);
  }

  const result = [];
  for (const nome of DEVS_FIXOS) {
    result.push(map.get(nome) ?? { nome, tickets: [] });
  }
  for (const [nome, d] of map.entries()) {
    if (!DEVS_FIXOS.includes(nome)) result.push(d);
  }

  return result.filter(d => d.tickets.some(t => STATUS_KEYS.has(t.status)));
}

/* ══════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════ */
export default function DevKanban() {
  const [devs,         setDevs]         = useState([]);
  const [footerTickets,setFooterTickets] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(false);
  const [countdown,    setCountdown]    = useState(REFRESH_SEC);
  const [footerOpen,   setFooterOpen]   = useState(false);
  const [dragging,     setDragging]     = useState(null);
  const [dragOverDev,  setDragOverDev]  = useState(null);
  const [dragCol,      setDragCol]      = useState(null);
  const [draggingCol,  setDraggingCol]  = useState(null);
  const [modal,        setModal]        = useState(null);
  const [toast,        setToast]        = useState(null);
  const [responsaveis, setResponsaveis] = useState([]);
  const loadRef = useRef(null);
  const isFirst = useRef(true);

  async function load(isRefresh = false) {
    const first = isFirst.current;
    if (first) setLoading(true);
    setError(false);
    if (isRefresh) clearCache();
    try {
      const [tickets, , programados] = await Promise.all([
        getSprintDados(),
        getSprintDevs(),
        getProgramados(),
      ]);
      const arr  = Array.isArray(tickets)     ? tickets     : [];
      const prog = Array.isArray(programados) ? programados : [];
      setDevs(buildDevList(arr));
      setFooterTickets(prog.filter(t => t.status === "Aguardando Distribuição"));
      isFirst.current = false;
    } catch {
      if (first) setError(true);
    } finally {
      if (first) setLoading(false);
    }
  }

  loadRef.current = load;
  function handleRefresh() { loadRef.current(true); setCountdown(REFRESH_SEC); }

  function showToast(ok, msg) {
    setToast({ ok, msg });
    setTimeout(() => setToast(null), 2500);
  }

  async function handleStatusDrop(ticket, targetCol) {
    const newStatus = targetCol.keys[0];
    if (targetCol.keys.includes(ticket.status)) return;
    setDevs(prev => prev.map(d => ({
      ...d,
      tickets: d.tickets.map(t => t.id === ticket.id ? { ...t, status: newStatus } : t),
    })));
    try {
      await updateTicketStatus(ticket.id, newStatus);
      showToast(true, `#${ticket.id} → ${targetCol.label}`);
    } catch {
      setDevs(prev => prev.map(d => ({
        ...d,
        tickets: d.tickets.map(t => t.id === ticket.id ? { ...t, status: ticket.status } : t),
      })));
      showToast(false, `Erro ao mover #${ticket.id}`);
    }
  }

  useEffect(() => {
    loadRef.current();
    setCountdown(REFRESH_SEC);
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(true); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    getSetupResponsaveis().then(setResponsaveis).catch(() => {});
  }, []);

  if (loading) return <Skeleton />;
  if (error) return (
    <div style={errorWrap}>
      <span style={{ fontSize: 32, color: "#fbbf24" }}>⚠</span>
      <p style={{ color: "#94a3b8" }}>Erro ao carregar tickets</p>
      <button onClick={handleRefresh} style={btnSec}>Tentar novamente</button>
    </div>
  );

  const total = devs.reduce((s, d) => s + d.tickets.filter(t => STATUS_KEYS.has(t.status)).length, 0);

  /* ── Carga do time ── */
  const _devsComTickets = devs.filter(d => d.tickets.some(t => STATUS_KEYS.has(t.status)));
  const mediaEquipe = _devsComTickets.length
    ? Math.round(_devsComTickets.reduce((s, d) => s + d.tickets.filter(t => STATUS_KEYS.has(t.status)).length, 0) / _devsComTickets.length)
    : 0;

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1 style={h1}>Kanban Developers</h1>
          <span style={pill}>{devs.length} devs</span>
          <span style={pill}>{total} tickets</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={handleRefresh} title="Atualizar agora" style={refreshBtn}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.18)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(96,165,250,0.08)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.2)"; }}
          >↻</button>
          <CountdownRing seconds={countdown} total={REFRESH_SEC} />
        </div>
      </header>

      {/* ── Board ── */}
      <div style={board}>
        {devs.length === 0
          ? <div style={{ color: "#475569", padding: 40 }}>Nenhum desenvolvedor com tickets</div>
          : devs.map((dev, i) => {
            const isCrossDevDrag = draggingCol && draggingCol.fromDevNome !== dev.nome;
            return (
              <DevSection
                key={dev.nome}
                dev={dev}
                colorIdx={i}
                isOver={dragOverDev === dev.nome && (!!dragging || isCrossDevDrag)}
                dragCol={dragCol}
                onDragOver={e => { e.preventDefault(); setDragOverDev(dev.nome); }}
                onDragLeave={() => setDragOverDev(null)}
                onDrop={() => {
                  setDragOverDev(null);
                  const nomeReal = DEV_ALIASES[dev.nome] ?? dev.nome;
                  if (dragging) {
                    setModal({ ticket: dragging, devNome: nomeReal, devLabel: dev.nome });
                    setDragging(null);
                  } else if (isCrossDevDrag) {
                    setModal({ ticket: draggingCol.ticket, devNome: nomeReal, devLabel: dev.nome });
                    setDraggingCol(null);
                    setDragCol(null);
                  }
                }}
                onColDragStart={(ticket) => setDraggingCol({ ticket, fromDevNome: dev.nome })}
                onColDragEnd={() => { setDraggingCol(null); setDragCol(null); }}
                onColDragOver={(colLabel) => {
                  if (!draggingCol || draggingCol.fromDevNome === dev.nome) {
                    setDragCol({ devNome: dev.nome, colLabel });
                  }
                }}
                onColDragLeave={() => setDragCol(null)}
                onColDrop={(col) => {
                  if (draggingCol) {
                    if (draggingCol.fromDevNome === dev.nome) {
                      handleStatusDrop(draggingCol.ticket, col);
                    } else {
                      const nr = DEV_ALIASES[dev.nome] ?? dev.nome;
                      setModal({ ticket: draggingCol.ticket, devNome: nr, devLabel: dev.nome });
                    }
                  }
                  setDraggingCol(null);
                  setDragCol(null);
                }}
              />
            );
          })
        }
      </div>

      {/* ── Rodapé: Aguardando Distribuição ── */}
      <div style={footer}>
        <button style={footerToggle} onClick={() => setFooterOpen(v => !v)}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#2563eb", flexShrink: 0 }} />
          <span style={{ color: "#93c5fd", fontWeight: 500, fontSize: 12 }}>
            Aguardando Distribuição
          </span>
          <span style={{
            background: "#2563eb", color: "#eff6ff",
            borderRadius: 99, padding: "2px 8px",
            fontSize: 11, fontWeight: 600,
            animation: footerTickets.length > 0 ? "kanbanPulse 2s ease-in-out infinite" : "none",
          }}>
            {footerTickets.length}
          </span>
          <span style={{
            marginLeft: "auto",
            width: 24, height: 24, borderRadius: 6,
            background: "rgba(255,255,255,0.06)",
            border: "0.5px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.4)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 10,
            transform: footerOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}>▼</span>
        </button>

        {footerOpen && (
          <div style={footerTicketRow}>
            {footerTickets.length === 0
              ? <div style={{ color: "rgba(255,255,255,0.2)", fontSize: 12, padding: "10px 16px" }}>Nenhum ticket aguardando distribuição</div>
              : footerTickets.map(t => (
                <div
                  key={t.id}
                  draggable
                  onDragStart={() => setDragging(t)}
                  onDragEnd={() => setDragging(null)}
                  onDoubleClick={() => window.open(`https://redmine.lexsis.com.br/issues/${t.id}`, "_blank")}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(37,99,235,0.09)"; e.currentTarget.style.borderColor = "rgba(37,99,235,0.3)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(37,99,235,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}
                  title="Duplo clique para abrir no Redmine · Arraste para atribuir a um desenvolvedor"
                  style={{
                    ...footerCard,
                    opacity: dragging?.id === t.id ? 0.5 : 1,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span style={{ fontFamily: "monospace", color: "#93c5fd", fontSize: 11, fontWeight: 500 }}>#{t.id}</span>
                    {t.grupo_entrega && (
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", fontFamily: "monospace" }}>
                        {t.grupo_entrega}
                      </span>
                    )}
                  </div>
                  {t.subject
                    ? <p style={{ fontSize: 11, color: "#D1D5DB", lineHeight: 1.4, margin: 0 }}>{t.subject}</p>
                    : <p style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", fontStyle: "italic", margin: 0 }}>#{t.id}</p>
                  }
                  {t.projeto && (
                    <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 4, display: "block" }}>📁 {t.projeto}</span>
                  )}
                </div>
              ))
            }
          </div>
        )}
      </div>

      {/* ── Modal de atribuição ── */}
      {modal && (
        <AtribuirModal
          ticket={modal.ticket}
          devNome={modal.devNome}
          devLabel={modal.devLabel ?? modal.devNome}
          mediaEquipe={mediaEquipe}
          responsaveis={responsaveis}
          devAbertos={(() => {
            const d = devs.find(d => d.nome === modal.devNome);
            return d ? d.tickets.filter(t => STATUS_KEYS.has(t.status)).length : 0;
          })()}
          onClose={() => setModal(null)}
          onConfirm={async (estimadoAnalise, size) => {
            setModal(null);
            try {
              await atribuirTicket(modal.ticket.id, modal.devNome, estimadoAnalise || null, size || null);
              setToast({ ok: true, msg: `#${modal.ticket.id} atribuído a ${modal.devNome}` });
              clearCache();
              loadRef.current(true);
            } catch {
              setToast({ ok: false, msg: `Erro ao atribuir #${modal.ticket.id}` });
            }
            setTimeout(() => setToast(null), 3000);
          }}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          padding: "10px 20px", borderRadius: 10, zIndex: 9999,
          background: toast.ok ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)",
          border: `1px solid ${toast.ok ? "rgba(74,222,128,0.4)" : "rgba(248,113,113,0.4)"}`,
          color: toast.ok ? "#4ade80" : "#f87171",
          fontSize: 13, fontWeight: 700, boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          backdropFilter: "blur(12px)", pointerEvents: "none",
        }}>
          {toast.ok ? "✓" : "✗"} {toast.msg}
        </div>
      )}

      <style>{`
        @keyframes kanbanPulse { 0%,100%{opacity:1} 50%{opacity:.6} }
      `}</style>
    </div>
  );
}

/* ── DevSection ──────────────────────────────────────────────────── */
function DevSection({ dev, colorIdx, isOver, dragCol, onDragOver, onDragLeave, onDrop, onColDragStart, onColDragEnd, onColDragOver, onColDragLeave, onColDrop }) {
  const byCol = {};
  for (const col of COLUNAS) byCol[col.label] = [];
  for (const t of dev.tickets) {
    const col = COLUNAS.find(c => c.keys.includes(t.status));
    if (col) byCol[col.label].push(t);
  }

  const total      = COLUNAS.reduce((s, c) => s + byCol[c.label].length, 0);
  const resolvidos = byCol["Resolvido"].length;
  const pct        = total ? Math.round(resolvidos / total * 100) : 0;
  const pctColor   = kanbanPctColor(pct);
  const av         = kanbanAvatarCores(dev.nome);

  return (
    <div
      style={{
        ...devSection,
        border: isOver ? "1px solid rgba(37,99,235,0.6)" : devSection.border,
        boxShadow: isOver ? "0 0 24px rgba(37,99,235,0.2), 0 8px 32px rgba(0,0,0,0.5)" : devSection.boxShadow,
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* ── Dev header ── */}
      <div style={{ ...devHeader, background: isOver ? "rgba(37,99,235,0.06)" : "rgba(255,255,255,0.02)", transition: "background 0.15s" }}>
        {/* Avatar com cores do mapa */}
        <div style={{ ...devAvatar, background: av.bg, color: av.color }}>
          {kanbanAvatarInitial(dev.nome)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={devName}>{dev.nome}</div>
          {/* Barra de progresso */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.07)", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: pctColor, borderRadius: 3, transition: "width 0.5s" }} />
            </div>
            <span style={{ color: pctColor, fontSize: 13, fontWeight: 500, minWidth: 36 }}>{pct}%</span>
            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 11 }}>{total} tickets</span>
          </div>
        </div>
      </div>

      {/* ── Colunas de status ── */}
      <div style={colsRow}>
        {COLUNAS.map(col => {
          const tickets    = byCol[col.label];
          const cs         = KAN_COL_STYLES[col.label] || {};
          const isColOver  = dragCol?.devNome === dev.nome && dragCol?.colLabel === col.label;
          const isEmpty    = tickets.length === 0;

          return (
            <div
              key={col.label}
              style={{
                ...statusCol,
                background:  isColOver ? `${col.color}0a` : cs.bg,
                borderTop:   cs.borderTop,
                borderRadius: "0 0 8px 8px",
                outline:     isColOver ? `1px dashed ${col.color}50` : undefined,
                border:      isEmpty && !isColOver ? `1.5px dashed rgba(255,255,255,0.06)` : undefined,
                transition:  "background 0.15s, outline 0.15s",
              }}
              onDragOver={e => { e.preventDefault(); onColDragOver(col.label); }}
              onDragLeave={onColDragLeave}
              onDrop={() => onColDrop(col)}
            >
              <div style={statusColHead}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: cs.textColor || col.color, flexShrink: 0 }} />
                <span style={{ color: cs.textColor || col.color, fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", flex: 1 }}>{col.label}</span>
                <span style={{
                  background:  tickets.length > 0 ? (cs.pillBg || `${col.color}25`)     : "rgba(255,255,255,0.05)",
                  color:       tickets.length > 0 ? (cs.pillColor || col.color)          : "rgba(255,255,255,0.25)",
                  borderRadius: 99, padding: "1px 7px", fontSize: 10, fontWeight: 500,
                }}>
                  {tickets.length}
                </span>
              </div>
              <div style={cardList}>
                {isEmpty
                  ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, padding: "24px 0" }}>
                      <span style={{ fontSize: 20, color: "rgba(255,255,255,0.1)", lineHeight: 1 }}>□</span>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.15)" }}>sem cards</span>
                    </div>
                  )
                  : tickets.map(t => (
                    <TicketCard
                      key={t.id}
                      ticket={t}
                      color={col.color}
                      onDragStart={onColDragStart}
                      onDragEnd={onColDragEnd}
                    />
                  ))
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── TicketCard ──────────────────────────────────────────────────── */
function diasEmStatus(ticket) {
  const ref = ticket.start_date || ticket.updated_on;
  if (!ref) return null;
  const dias = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
  if (dias < 0) return null;
  if (dias === 0) return "hoje";
  if (dias === 1) return "1 dia";
  return `${dias} dias`;
}

function TicketCard({ ticket, color, onDragStart, onDragEnd }) {
  const [over, setOver] = useState(false);
  const emCurso = ticket.status === "Em curso";
  const tempo   = emCurso ? diasEmStatus(ticket) : null;
  const tempoColor = (() => {
    if (!tempo || tempo === "hoje") return "#4ade80";
    const d = parseInt(tempo);
    if (d <= 2) return "#4ade80";
    if (d <= 5) return "#fbbf24";
    return "#f87171";
  })();

  const borderColor = kanbanBorderColor(ticket);
  const idColor     = kanbanIdColor(ticket);

  return (
    <div
      draggable
      onDragStart={() => onDragStart?.(ticket)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => setOver(true)}
      onMouseLeave={() => setOver(false)}
      onDoubleClick={() => window.open(`https://redmine.lexsis.com.br/issues/${ticket.id}`, "_blank")}
      title="Arraste para mudar situação · Duplo clique para abrir no Redmine"
      style={{
        background:   over ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)",
        border:       `0.5px solid ${over ? "rgba(255,255,255,0.13)" : "rgba(255,255,255,0.08)"}`,
        borderLeft:   `3px solid ${borderColor}`,
        borderRadius: "0 8px 8px 0",
        padding:      "10px 12px",
        marginBottom: 8,
        cursor:       "grab",
        userSelect:   "none",
        transition:   "all 0.15s ease",
        display:      "flex", flexDirection: "column", gap: 4,
      }}
    >
      {/* Linha 1: ID + sprint badge */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 500, color: idColor }}>#{ticket.id}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {tempo && (
            <span style={{ fontSize: 9, fontWeight: 700, color: tempoColor, background: `${tempoColor}15`, border: `1px solid ${tempoColor}40`, borderRadius: 6, padding: "1px 6px" }}>
              ⏱ {tempo}
            </span>
          )}
          {ticket.grupo_entrega && (
            <span style={{ fontFamily: "monospace", fontSize: 10, color: "rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)", padding: "1px 6px", borderRadius: 4 }}>
              {ticket.grupo_entrega}
            </span>
          )}
        </div>
      </div>
      {/* Título */}
      {ticket.subject
        ? <p style={{ fontSize: 12, color: "#D1D5DB", lineHeight: 1.45, margin: 0, marginTop: 6 }}>{ticket.subject}</p>
        : <p style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", fontStyle: "italic", margin: 0, marginTop: 6 }}>Ver no Redmine →</p>
      }
      {ticket.projeto && (
        <span style={{ fontSize: 9, color: "rgba(255,255,255,0.25)" }}>📁 {ticket.projeto}</span>
      )}
    </div>
  );
}

/* ── AtribuirModal ───────────────────────────────────────────────── */
const SIZE_OPTS  = ["P", "M", "G", "XG"];
const SIZE_LABEL = { P: "Pequeno · 1h", M: "Médio · 9h", G: "Grande · 18h", XG: "Extra Grande · 36h" };

function AtribuirModal({ ticket, devNome, devLabel, mediaEquipe, devAbertos, responsaveis, onClose, onConfirm }) {
  const [saving,          setSaving]          = useState(false);
  const [erro,            setErro]            = useState(null);
  const [estimadoAnalise, setEstimadoAnalise] = useState("");
  const [size,            setSize]            = useState(ticket.size ? String(ticket.size).toUpperCase().trim() : "");
  const [responsavel,     setResponsavel]     = useState(ticket.responsavel || "");

  async function handleConfirm() {
    setSaving(true); setErro(null);
    try {
      if (responsavel !== (ticket.responsavel || "")) {
        await putTicketResponsavel(ticket.id, responsavel);
      }
      await onConfirm(estimadoAnalise, size);
    } catch {
      setErro("Erro ao atribuir ticket. Tente novamente.");
      setSaving(false);
    }
  }

  const displayName = devLabel ?? devNome;
  const av = kanbanAvatarCores(displayName);

  return (
    <div style={overlay} onClick={saving ? undefined : onClose}>
      <div style={modalBox} onClick={e => e.stopPropagation()}>

        {/* Cabeçalho */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ color: "#E5E7EB", fontSize: 15, fontWeight: 500, margin: 0 }}>Atribuir Ticket</h2>
          <button
            onClick={onClose}
            disabled={saving}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(226,75,74,0.15)"; e.currentTarget.style.color = "#F09595"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
            style={{
              width: 28, height: 28, borderRadius: 6,
              background: "rgba(255,255,255,0.06)",
              border: "0.5px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.4)",
              fontSize: 12, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s",
            }}
          >✕</button>
        </div>

        {/* Card do ticket */}
        <div style={{
          background: "rgba(255,255,255,0.04)",
          border: "0.5px solid rgba(255,255,255,0.08)",
          borderLeft: `3px solid ${ticket.status === "Em curso" ? "#fbbf24" : "#2563eb"}`,
          borderRadius: "0 8px 8px 0",
          padding: "12px 14px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: "monospace", color: "#93c5fd", fontSize: 12, fontWeight: 500 }}>#{ticket.id}</span>
            {ticket.status && (
              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 99, border: "1px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.04)" }}>
                {ticket.status}
              </span>
            )}
            {(ticket.programado === 1 || ticket.programado === "1" || ticket.programado === true) && (
              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 99, border: "1px solid rgba(34,211,238,0.2)", color: "#22d3ee", background: "rgba(34,211,238,0.06)" }}>
                Programado
              </span>
            )}
          </div>
          {ticket.subject && (
            <p style={{ color: "#E5E7EB", fontSize: 13, margin: "4px 0 6px", lineHeight: 1.4 }}>{ticket.subject}</p>
          )}
          {ticket.projeto && (
            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10, display: "flex", alignItems: "center", gap: 4 }}>
              📁 {ticket.projeto}
            </span>
          )}
          {ticket.desenvolvedor && (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.5 }}>Atribuído atualmente:</span>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{ticket.desenvolvedor}</span>
            </div>
          )}
        </div>

        {/* Seta */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "rgba(37,99,235,0.2)",
            border: "0.5px solid rgba(37,99,235,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#93c5fd", fontSize: 14,
          }}>↓</div>
        </div>

        {/* Atribuir para */}
        <div style={{
          background: "rgba(255,255,255,0.04)",
          border: "0.5px solid rgba(255,255,255,0.1)",
          borderRadius: 8, padding: "12px 14px",
        }}>
          <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)", marginBottom: 6 }}>
            Atribuir para
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, color: "#E5E7EB" }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {kanbanAvatarInitial(displayName)}
            </div>
            <span style={{ color: "#60a5fa", fontWeight: 800, fontSize: 15 }}>{displayName}</span>
          </div>
        </div>

        {/* Responsável (custom field Redmine) */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Responsável
          </label>
          <select
            value={responsavel}
            onChange={e => setResponsavel(e.target.value)}
            disabled={saving}
            style={{
              padding: "9px 12px", borderRadius: 8,
              border: "1px solid rgba(96,165,250,0.2)",
              background: "rgba(11,22,42,0.7)",
              color: "#e2e8f0", fontSize: 13, outline: "none",
              fontFamily: "Inter, sans-serif", cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            <option value="">— sem responsável —</option>
            {responsaveis.map(nome => (
              <option key={nome} value={nome}>{nome}</option>
            ))}
          </select>
        </div>

        {/* Carga do desenvolvedor */}
        {mediaEquipe > 0 && (() => {
          const apos = devAbertos + 1;
          const acima = apos > mediaEquipe;
          const muitoAcima = apos > mediaEquipe * 1.5;
          const barMax = Math.max(mediaEquipe * 2, apos + 1);
          const pctAtual = Math.min((devAbertos / barMax) * 100, 100);
          const pctApos  = Math.min((apos / barMax) * 100, 100);
          const barColor = muitoAcima ? "#f87171" : acima ? "#fb923c" : "#4ade80";
          return (
            <div style={{
              padding: "10px 12px", borderRadius: 8,
              background: muitoAcima ? "rgba(248,113,113,0.06)" : acima ? "rgba(251,146,60,0.06)" : "rgba(74,222,128,0.05)",
              border: `1px solid ${muitoAcima ? "rgba(248,113,113,0.25)" : acima ? "rgba(251,146,60,0.25)" : "rgba(74,222,128,0.2)"}`,
              display: "flex", flexDirection: "column", gap: 8,
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.7 }}>
                Carga do desenvolvedor
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94a3b8" }}>
                <span>Abertos atualmente: <strong style={{ color: "#E5E7EB" }}>{devAbertos}</strong></span>
                <span>Média do time: <strong style={{ color: "#E5E7EB" }}>{mediaEquipe}</strong></span>
                <span>Após atribuição: <strong style={{ color: barColor }}>{apos}</strong></span>
              </div>
              <div style={{ position: "relative", height: 6, borderRadius: 4, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pctAtual}%`, background: "rgba(96,165,250,0.4)", borderRadius: 4, transition: "width 0.3s" }} />
                <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pctApos}%`, background: barColor, borderRadius: 4, opacity: 0.7, transition: "width 0.3s" }} />
                <div style={{ position: "absolute", top: -2, bottom: -2, width: 2, borderRadius: 1, background: "#60a5fa", left: `${Math.min((mediaEquipe / barMax) * 100, 100)}%` }} />
              </div>
              {acima && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: muitoAcima ? "#f87171" : "#fb923c", fontWeight: 700 }}>
                  {muitoAcima ? "⛔" : "⚠"} {muitoAcima ? "Acima da capacidade — considere redistribuir" : "Acima da média do time — avalie antes de confirmar"}
                </div>
              )}
            </div>
          );
        })()}

        {/* Estimativa de desenvolvimento */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Estimativa de desenvolvimento (horas)
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="number"
              min="0"
              step="0.5"
              placeholder="Ex: 2.5"
              value={estimadoAnalise}
              onChange={e => setEstimadoAnalise(e.target.value)}
              disabled={saving}
              style={{
                flex: 1, padding: "9px 12px", borderRadius: 8,
                border: "1px solid rgba(96,165,250,0.2)",
                background: "rgba(11,22,42,0.7)",
                color: "#e2e8f0", fontSize: 14, outline: "none",
                fontFamily: "Inter, sans-serif",
              }}
            />
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", whiteSpace: "nowrap" }}>h</span>
          </div>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.2)" }}>
            Campo E_Estimado_Analise — preenchido pelo responsável pela sprint, antes do desenvolvedor receber o ticket.
          </span>
        </div>

        {/* Size */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: 0.7 }}>
            Size
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            {SIZE_OPTS.map(opt => {
              const active = size === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  disabled={saving}
                  onClick={() => setSize(active ? "" : opt)}
                  title={SIZE_LABEL[opt]}
                  style={{
                    flex: 1, padding: "8px 0", borderRadius: 8,
                    border: active ? "1px solid rgba(96,165,250,0.55)" : "1px solid rgba(96,165,250,0.15)",
                    background: active ? "rgba(96,165,250,0.18)" : "rgba(11,22,42,0.5)",
                    color: active ? "#93c5fd" : "rgba(255,255,255,0.3)",
                    fontSize: 13, fontWeight: active ? 800 : 500,
                    cursor: saving ? "not-allowed" : "pointer",
                    transition: "all 0.15s", letterSpacing: 0.5,
                  }}
                >{opt}</button>
              );
            })}
          </div>
          {size && <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{SIZE_LABEL[size]}</span>}
        </div>

        {erro && (
          <div style={{ color: "#f87171", fontSize: 12, padding: "8px 12px", borderRadius: 8, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)" }}>
            ⚠ {erro}
          </div>
        )}

        {/* Botões */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            disabled={saving}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "rgba(255,255,255,0.45)"; }}
            style={btnSec}
          >Cancelar</button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            onMouseEnter={e => { e.currentTarget.style.background = "#3C3489"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#2563eb"; }}
            style={{ ...btnConfirm, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Atribuindo..." : "Confirmar atribuição"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── CountdownRing ───────────────────────────────────────────────── */
function CountdownRing({ seconds, total }) {
  const r = 11, circ = 2 * Math.PI * r, filled = circ * (seconds / total);
  const color = seconds <= 10 ? "#f87171" : "#60a5fa";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg width={30} height={30} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
        <circle cx={15} cy={15} r={r} fill="none" stroke="rgba(96,165,250,0.1)" strokeWidth={2.5} />
        <circle cx={15} cy={15} r={r} fill="none" stroke={color} strokeWidth={2.5}
          strokeDasharray={circ} strokeDashoffset={circ - filled} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }} />
      </svg>
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
        <span style={{ color, fontSize: 14, fontWeight: 800, letterSpacing: -0.5 }}>
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <span style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>próx. atualiz.</span>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={page}>
      <div style={{ height: 32, width: 260, borderRadius: 10, background: "rgba(255,255,255,0.04)", marginBottom: 16 }} />
      <div style={{ display: "flex", gap: 16, flex: 1 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 760, borderRadius: 14, background: "rgba(255,255,255,0.03)", flexShrink: 0 }} />)}
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────── */
const page = { display: "flex", flexDirection: "column", gap: 12, height: "calc(100vh - 56px)", overflow: "hidden" };
const h1   = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5 };
const pill = {
  padding: "3px 10px", borderRadius: 99,
  background: "rgba(255,255,255,0.06)",
  border: "0.5px solid rgba(255,255,255,0.1)",
  color: "rgba(255,255,255,0.5)",
  fontSize: 11, fontWeight: 500,
};
const refreshBtn = { width: 34, height: 34, borderRadius: 8, border: "1px solid rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.08)", color: "#60a5fa", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s, border-color 0.15s", flexShrink: 0, lineHeight: 1 };

const board = {
  display: "flex", gap: 14,
  overflowX: "auto", overflowY: "hidden",
  flex: 1, paddingBottom: 8, alignItems: "stretch",
};

const devSection = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.12)",
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
  flexShrink: 0, display: "flex", flexDirection: "column",
  overflow: "hidden", width: 640,
};

const devHeader = {
  display: "flex", alignItems: "flex-start", gap: 14,
  padding: "16px 18px 14px",
  borderBottom: "1px solid rgba(96,165,250,0.08)",
};

const devAvatar = {
  width: 44, height: 44, borderRadius: 12,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontWeight: 700, fontSize: 16,
  flexShrink: 0,
};

const devName = { color: "#f1f5f9", fontSize: 15, fontWeight: 800, letterSpacing: -0.3 };

const colsRow = {
  display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
  gap: 0, flex: 1, overflow: "hidden",
};

const statusCol = {
  display: "flex", flexDirection: "column",
  borderRight: "1px solid rgba(96,165,250,0.06)",
  overflow: "hidden",
};

const statusColHead = {
  display: "flex", alignItems: "center", gap: 6,
  padding: "10px 12px 8px",
  borderBottom: "1px solid rgba(96,165,250,0.06)",
  background: "rgba(0,0,0,0.15)",
  flexShrink: 0,
};

const cardList = {
  display: "flex", flexDirection: "column",
  padding: "10px 10px",
  overflowY: "auto", flex: 1,
};

const errorWrap = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12 };
const btnSec    = { padding: "8px 16px", borderRadius: 8, border: "0.5px solid rgba(255,255,255,0.12)", background: "transparent", color: "rgba(255,255,255,0.45)", fontSize: 13, cursor: "pointer", transition: "all 0.15s" };
const btnConfirm = { padding: "8px 20px", borderRadius: 8, border: "none", background: "#2563eb", color: "#eff6ff", fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "background 0.15s" };

/* Rodapé */
const footer = {
  flexShrink: 0,
  background: "rgba(37,99,235,0.06)",
  borderTop: "2px solid rgba(37,99,235,0.35)",
  borderRadius: 12,
};
const footerToggle = {
  width: "100%", display: "flex", alignItems: "center", gap: 8,
  padding: "10px 16px", background: "none", border: "none", cursor: "pointer",
};
const footerTicketRow = {
  display: "flex", gap: 10, overflowX: "auto",
  padding: "0 14px 12px", paddingTop: 4,
};
const footerCard = {
  background: "rgba(37,99,235,0.04)",
  border: "1.5px dashed rgba(255,255,255,0.12)",
  borderLeft: "3px solid rgba(37,99,235,0.5)",
  borderRadius: 8, padding: "8px 10px",
  minWidth: 200, maxWidth: 240, flexShrink: 0,
  cursor: "grab", userSelect: "none",
  transition: "background 0.15s, border-color 0.15s",
};

/* Modal */
const overlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 };
const modalBox = { background: "#13151C", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "20px 24px", width: 440, maxWidth: "92vw", boxShadow: "0 28px 72px rgba(0,0,0,0.8)", display: "flex", flexDirection: "column", gap: 14 };
